import {
  DeleteOutlined,
  DownloadOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import {
  Button,
  Flex,
  Form,
  Space,
  Spin,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ProductModelFile } from '@app/api/Products';
import { FRONT_WARNING, SUCCESS } from '@app/constants/alerts';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import {
  useAttachFilesMutation,
  useRemoveFilesMutation,
  useGetFilesMutation,
} from '@app/services/rtkQuery/notifications';
import type { NRecordReminders } from '@app/types';

import type { UploadProps } from 'antd/es/upload/interface';

const { Text } = Typography;
const { Item } = Form;
const { Dragger } = Upload;

const MAX_FILE_SIZE_BYTES = 300 * 1024 * 1024;
const ALLOWED_EXTENSIONS: string[] = [
  'xlsx',
  'xls',
  'jpg',
  'jpeg',
  'png',
  'pdf',
  'doc',
  'docx',
  'txt',
  'mp4',
  'webp',
];

const ACCEPTED_FILE_TYPES = ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(
  ','
);

type ReminderFilesProps = {
  reminder: NRecordReminders.ModelRecordReminder | null;
  componentClassName: string;
  disabled?: boolean;
};

export const ReminderFiles: React.FC<ReminderFilesProps> = ({
  reminder,
  componentClassName,
  disabled = false,
}) => {
  const { activeBranchId } = useUserPrint();
  const { showNotification } = useNotification();

  const [files, setFiles] = useState<ProductModelFile[]>([]);

  const [attachFiles, { isLoading: isAttachLoading }] =
    useAttachFilesMutation();
  const [removeFiles, { isLoading: isRemoveLoading }] =
    useRemoveFilesMutation();
  const [getFiles, { isLoading: isGetFilesLoading }] = useGetFilesMutation();

  const isFilesLoading =
    isAttachLoading || isRemoveLoading || isGetFilesLoading;

  const hasReminderContext = Boolean(reminder?.Id) && Boolean(activeBranchId);

  const fetchFiles = useCallback(
    async (FileIds?: number[] | null | undefined) => {
      if (!hasReminderContext || !reminder) {
        setFiles([]);
        return;
      }

      try {
        const response = await getFiles({
          ActiveBranchId: activeBranchId,
          Id: reminder.Id,
          FileIds: FileIds || reminder.FileIds || [],
        }).unwrap();

        setFiles(response.Files ?? []);
      } catch (error) {
        showNotification(error);
      }
    },
    [activeBranchId, getFiles, hasReminderContext, reminder, showNotification]
  );

  useEffect(() => {
    if (
      !hasReminderContext ||
      !reminder?.FileIds ||
      reminder.FileIds.length === 0
    ) {
      setFiles([]);
      return;
    }

    void fetchFiles();
  }, [fetchFiles, hasReminderContext, reminder?.Id, reminder?.FileIds]);

  const readFileAsBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        if (typeof reader.result === 'string') {
          const parts = reader.result.split(',');
          const base64 = parts.length > 1 ? parts[1] : parts[0];
          resolve(base64);
        } else {
          reject(new Error('Не удалось прочитать файл'));
        }
      };

      reader.onerror = () => {
        if (reader.error) {
          reject(reader.error);
        } else {
          reject(new Error('Не удалось прочитать файл'));
        }
      };

      reader.readAsDataURL(file);
    });
  }, []);

  const handleDownload = useCallback(
    (file: ProductModelFile) => {
      if (!file.Data) {
        showNotification('Файл недоступен для скачивания', FRONT_WARNING);
        return;
      }

      try {
        const binaryString = window.atob(file.Data);
        const { length } = binaryString;
        const bytes = new Uint8Array(length);

        for (let i = 0; i < length; i += 1) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const blob = new Blob([bytes], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = file.Name ?? 'file';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch {
        showNotification(
          'Произошла ошибка при скачивании файла',
          FRONT_WARNING
        );
      }
    },
    [showNotification]
  );

  const handleRemoveFile = useCallback(
    async (fileId: number | undefined) => {
      if (!hasReminderContext || !reminder || !fileId) {
        return;
      }

      try {
        await removeFiles({
          ActiveBranchId: activeBranchId,
          Id: reminder.Id,
          FileIds: [fileId],
        }).unwrap();

        await fetchFiles();

        showNotification('Файл откреплён от уведомления', SUCCESS);
      } catch (error) {
        showNotification(error);
      }
    },
    [
      activeBranchId,
      fetchFiles,
      hasReminderContext,
      removeFiles,
      reminder,
      showNotification,
    ]
  );

  const handleUpload = useCallback(
    async (file: File) => {
      if (!hasReminderContext || !reminder) {
        showNotification(
          'Не удалось определить уведомление для прикрепления файла',
          FRONT_WARNING
        );
        return;
      }

      try {
        const base64 = await readFileAsBase64(file);

        const modelFile: ProductModelFile = {
          Id: 0,
          Name: file.name,
          Data: base64,
          Description: null,
          Lifetime: null,
        };

        const result = await attachFiles({
          ActiveBranchId: activeBranchId,
          Id: reminder.Id,
          Files: [modelFile],
        }).unwrap();

        await fetchFiles(result.FileIds);

        showNotification('Файл прикреплён к уведомлению', SUCCESS);
      } catch (error) {
        showNotification(error);
      }
    },
    [
      activeBranchId,
      attachFiles,
      fetchFiles,
      hasReminderContext,
      readFileAsBase64,
      reminder,
      showNotification,
    ]
  );

  const uploadProps: UploadProps = useMemo(
    () => ({
      multiple: true,
      showUploadList: false,
      accept: ACCEPTED_FILE_TYPES,
      beforeUpload: (file) => {
        const extension = file.name.split('.').pop()?.toLowerCase() ?? '';

        if (!ALLOWED_EXTENSIONS.includes(extension)) {
          showNotification(
            'Недопустимый формат файла. Разрешены: xlsx, xls, jpg, png, jpeg, pdf, doc, docx, txt, mp4, webp',
            FRONT_WARNING
          );
          return Upload.LIST_IGNORE;
        }

        if (file.size > MAX_FILE_SIZE_BYTES) {
          showNotification(
            'Превышен максимальный размер файла 300 МБ',
            FRONT_WARNING
          );
          return Upload.LIST_IGNORE;
        }

        void handleUpload(file as File);
        return false;
      },
      disabled: !hasReminderContext || isFilesLoading,
    }),
    [handleUpload, hasReminderContext, isFilesLoading, showNotification]
  );

  return (
    <div className={`${componentClassName}__files`}>
      <Text
        className={`${componentClassName}__label`}
        style={{ marginBottom: '12px !important' }}
      >
        Файлы уведомления
      </Text>

      <Item
        className={`${componentClassName}__files-upload`}
        extra="Максимальный размер файла — 300 МБ. Допустимые форматы: xlsx, xls, jpg, png, jpeg, pdf, doc, docx, txt, mp4, webp."
      >
        <Spin spinning={isFilesLoading}>
          <Dragger {...uploadProps} disabled={disabled}>
            <Space direction="vertical">
              <PaperClipOutlined />
              <span>Перетащите файлы сюда или нажмите, чтобы выбрать</span>
            </Space>
          </Dragger>
        </Spin>
      </Item>

      {files.length > 0 && (
        <Space
          direction="vertical"
          size={8}
          className={`${componentClassName}__files-list`}
        >
          {files.map((file) => (
            <Flex
              key={file.Id ?? file.Name}
              className={`${componentClassName}__files-list-item`}
              align="center"
              justify="space-between"
            >
              <Text>{file.Name}</Text>
              <Space size={4}>
                <Tooltip title="Скачать файл">
                  <Button
                    type="text"
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownload(file)}
                    disabled={!file.Data || disabled}
                  />
                </Tooltip>
                <Tooltip title="Удалить файл">
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveFile(file.Id)}
                    disabled={disabled}
                  />
                </Tooltip>
              </Space>
            </Flex>
          ))}
        </Space>
      )}
    </div>
  );
};
